import { Hermes } from 'apollo-cache-hermes';
import { ApolloClient } from 'apollo-client';
import { ApolloLink, split } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import { WebSocketLink } from 'apollo-link-ws';
import fetch from 'isomorphic-fetch';
import { getMainDefinition } from 'apollo-utilities';

let apolloClient: ApolloClient<any> | null = null;

// Polyfill fetch() on the server (used by apollo-client)
if (!process.browser) {
  // @ts-ignore
  global.fetch = fetch;
}

interface Options {
  getAuthToken: () => string | null;
  apiUrl?: string;
  wsApiUrl?: string;
}

function create(
  initialState: object | null,
  {
    getAuthToken,
    apiUrl = process.env.apiUrl,
    wsApiUrl = process.env.wsApiUrl,
  }: Options,
) {
  const httpLink = createHttpLink({
    uri: apiUrl,
    credentials: 'same-origin',
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) =>
        // eslint-disable-next-line no-console
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.log(`[Network error]: ${networkError}`);
    }
  });

  const authLink = setContext((_, { headers }) => {
    const token = getAuthToken();

    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const httpAuthLink = authLink.concat(httpLink);

  const transports = [errorLink];

  // should only connect to ws from browser
  if (process.browser && wsApiUrl) {
    const wsLink = new WebSocketLink({
      uri: wsApiUrl,
      options: {
        lazy: true,
        reconnect: true,
        reconnectionAttempts: 10,
        connectionParams: () => {
          const token = getAuthToken();
          if (!token) {
            throw new Error("Can't subscribe no token");
          }
          return {
            authorization: token ? `Bearer ${token}` : '',
          };
        },
      },
    });

    const link = split(
      // split based on operation type
      ({ query }) => {
        // @ts-ignore
        const { kind, operation } = getMainDefinition(query);
        return kind === 'OperationDefinition' && operation === 'subscription';
      },
      wsLink,
      httpAuthLink,
    );

    transports.push(link);
  } else {
    transports.push(httpAuthLink);
  }

  const allLink: any = ApolloLink.from(transports);

  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    connectToDevTools: process.browser,
    ssrMode: !process.browser, // Disables forceFetch on the server (so queries are only run once)
    link: allLink,
    cache: new Hermes({
      resolverRedirects: {
        Query: {
          node: ({ id }) => id,
        },
      },
      addTypename: true,
      freeze: true,
    }).restore(initialState || {}),
  });
}

export default function initApollo(
  initialState: object | null,
  options: Options,
) {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (!process.browser) {
    return create(initialState, options);
  }

  // Reuse client on the client-side
  if (!apolloClient) {
    apolloClient = create(initialState, options);
  }

  return apolloClient;
}
